import { useEffect, useMemo, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { BaseModal } from "../../components/Modal/BaseModal";
import { EditableGrid } from "../../components/DataTable/EditableGrid";
import { Add } from "@mui/icons-material";
import moment from "moment";
import { useAppDispatch } from "../../store/hooks";
import { fetchProductPriceUnits } from "../../slices/productcostapplication.slice";
import { validateHarvestPricePeriodsGridData } from "../Modals/action-bill-modal/util";
import { v4 as uuidv4 } from "uuid";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import { ActionCell } from "../../components/DataTable/customcell/ActionCell";
import { saveHarvestCostData } from "../../slices/productcostseeds.slice";
import { PriceCell } from "./PriceCell";
import { GridColumnProps } from "@progress/kendo-react-grid";

interface Props {
  open: boolean;
  handleClose: () => void;
  editItem: any;
}
const newRow = {
  notes: "",
  firstHarvested: new Date(),
  lastHarvested: new Date(),
};

export const EditHarvestPriceModal = ({
  handleClose,
  open,
  editItem,
}: Props) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchProductPriceUnits());
  }, [dispatch]);

  useEffect(() => {
    if (editItem) {
      const { periods } = editItem;
      setTableData(
        periods.map((item: any) => {
          return {
            ...item,
            id: uuidv4(),
            firstHarvested: moment(item.firstHarvested, "MM/DD/YYYY").toDate(),
            lastHarvested: moment(item.lastHarvested, "MM/DD/YYYY").toDate(),
          };
        })
      );
    }
  }, [editItem]);

  const addNewPeriod = () => {
    setTableData((prev) => [...prev, { ...newRow, id: uuidv4() }]);
  };

  const onItemChange = (e: any) => {
    const { field, value, dataItem } = e;
    const updatedData = tableData.map((item: any) => {
      if (item.id === dataItem.id) {
        const updatedRow = {
          ...item,
        };
        updatedRow[field] = value;
        return updatedRow;
      }
      return item;
    });
    setTableData(updatedData);
  };

  useEffect(() => {
    setErrorFields(
      validateHarvestPricePeriodsGridData(tableData)
    );
    setEdit(
      tableData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [tableData]);

  const handleSave = async () => {
    const requestData = {
      periods: tableData.map((item: any) => {
        return {
          harvestPrice: item.harvestPrice,
          haulingPrice: item.haulingPrice,
          haulingOveragePrice: item.haulingOveragePrice,
          overageLimit: item.overageLimit,
          firstHarvested: moment(item.firstHarvested).format("MM/DD/YYYY"),
          lastHarvested: moment(item.lastHarvested).format("MM/DD/YYYY"),
          notes: item.notes,
        };
      }),
    };
    await dispatch(
      saveHarvestCostData({
        ...requestData,
        id: editItem.id,
        cropName: editItem.cropName,
        rateType: editItem.rateType,
      })
    );

    handleClose();
  };

  const deletePricePeriod = (dataItem: any) => {
    setTableData((prevTableData) => prevTableData.filter((item: any) => item.id !== dataItem.id));
  };

  const columns: GridColumnProps[] = useMemo(() => [
              {
                field: "harvestPrice",
                title: "Harvest Price",
                width: 150,
                cells:{data: (props) => <PriceCell {...props} endAdornment="/acre"/>},
              },
              {
                field: "haulingPrice",
                title: "Hauling Price",
                width: 150,
                cells:{data: (props) => <PriceCell {...props} endAdornment="/bu"/>},
              },
              {
                field: "overageLimit",
                title: "Overage Limit",
                width: 120,
                cells:{data: (props) => <PriceCell {...props} startAdornment="" endAdornment="bu"/>}
              },
              {
                field: "haulingOveragePrice",
                title: "Overage Price",
                width: 150,
                cells:{data: (props) => <PriceCell {...props} endAdornment="/bu"/>}
              },
              {
                field: "firstHarvested",
                title: "First Planted Date",
                editor: "date",
                format: "{0:d}",
                width: 150,
              },
              {
                field: "lastHarvested",
                title: "Last Planted Date",
                editor: "date",
                format: "{0:d}",
                width: 150,
              },
              {
                field: "notes",
                title: "Notes",
                width: 180,
              },
              {
                title: "",
                width: 100,
                cells: {
                  data: (props) => (
                    <ActionCell {...props} onDelete={deletePricePeriod} />
                  ),
                },
              },
            ], []);

  return (
    <BaseModal open={open} handleClose={handleClose}>
      <Box display={"flex"} flexDirection={"column"} gap={5}>
        <Typography variant="h6">Edit Harvest Price</Typography>
        <Box display={"flex"} flexWrap={"wrap"} gap={2}>
          <TextField
            label="Crop Name"
            variant="outlined"
            defaultValue={editItem?.cropName ?? ""}
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            label="Rate Type"
            variant="outlined"
            defaultValue={editItem?.rateType ?? ""}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>
        <Box>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            mb={1}
            alignItems={"center"}
          >
            <Typography>Price Periods</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={addNewPeriod}
            >
              Add Period
            </Button>
          </Box>
          <EditableGrid
            dataItemKey="id"
            onItemChange={onItemChange}
            edit={edit}
            columns={columns}
            data={tableData}
          />
        </Box>
        <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
          <Button
            variant="contained"
            color="primary"
            disabled={!(tableData.length && !errorFields.length)}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button variant="outlined" color="error" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};
